import { z } from "zod/v4";
import { costRecipeSchema, deriveCostRecipe, materialExtendedPrice } from "./cost-recipe";
import { quantityDerivationSchema } from "./quantity-dimensions";

export const proposedMaterialSchema = z.object({
  name: z.string().trim().min(1).max(160),
  specification: z.string().trim().max(300).default(""),
  quantity: z.number().finite().positive().max(1_000_000),
  unit: z.string().trim().min(1).max(30),
  unitPrice: z.number().finite().min(0).max(99_999_999.99),
  assumption: z.string().trim().min(1).max(400),
});
export type ProposedMaterial = z.infer<typeof proposedMaterialSchema>;
export const materialProposalSchema = z.object({
  materials: z.array(proposedMaterialSchema).max(30),
  noMaterialsReason: z.preprocess(value => value === null ? undefined : value, z.string().trim().max(400).optional()),
}).superRefine((value, ctx) => {
  if (!value.materials.length && !value.noMaterialsReason?.trim())
    ctx.addIssue({ code: "custom", message: "Explain why no supplied materials are needed." });
});
export const proposedMaterialsTotal = (materials: ProposedMaterial[]) =>
  materials.reduce((total, material) => total + Math.round(Number(materialExtendedPrice(material.quantity, material.unitPrice)) * 100), 0) / 100;

// Provenance is assigned by the endpoint, never taken from an AI response.
export const materialProposalResponseSchema = materialProposalSchema.and(z.object({
  source: z.enum(["library", "estimated", "unavailable"]).optional(),
}));

export const materialProposalRequestSchema = z.object({
  libraryOnly: z.boolean().optional(),
  line: z.object({
    name: z.string().trim().min(1).max(200), description: z.string().max(4000).nullish(),
    category: z.string().max(200), quantity: z.number().finite().positive().max(1_000_000),
    unit: z.string().min(1).max(50), unitCost: z.number().finite().min(0),
    materialCost: z.number().finite().min(0).optional(),
    quantityDerivation: quantityDerivationSchema.nullish(),
    costLibraryTaskId: z.string().max(200).nullish(),
  }),
});

export const materialAllowanceApprovalSchema = z.object({
  version: z.literal(1),
  amount: z.number().finite().min(0).max(99_999_999.99).refine(n => Math.abs(n * 100 - Math.round(n * 100)) < 0.00001),
  source: z.enum(["estimated", "builder", "library"]),
  libraryChecked: z.boolean().optional(),
  orderQuantities: z.record(z.string().max(2000), z.object({
    quantity: z.number().finite().min(0).max(1_000_000),
    requiredQuantity: z.number().finite().min(0),
  })).refine(value => Object.keys(value).length <= 200).optional(),
  basis: z.string().max(30000),
  approved: z.boolean().optional(),
  manualApproved: z.boolean().optional(),
  requiresApproval: z.boolean().optional(),
  builderWithheld: z.boolean().optional(),
  mode: z.literal("automatic").optional(),
  recipeApproval: z.boolean().optional(),
  materials: z.array(proposedMaterialSchema).max(30).optional(),
  noMaterialsReason: z.string().trim().max(400).optional(),
});
export type MaterialAllowanceApproval = z.infer<typeof materialAllowanceApprovalSchema>;

/** Scope evidence, not category or a missing material-cost allocation. Never
 * discard explicit purchases or a builder's withheld/unfinished material edits. */
export function scopeNeedsNoPurchaseMaterials(line: {
  name: string; description?: string | null; quoteCostRecipe?: unknown; materialAllowanceApproval?: unknown;
}): boolean {
  if (!/^(?:remove|removal\b(?:\s+of)?|demolish|demolition\b(?:\s+of)?|dismantle|strip(?:\s+out|\s+off)?|break\s+out|lift\s+(?:out|up))\b/i.test(line.name.trim())) return false;
  const recipe = costRecipeSchema.safeParse(line.quoteCostRecipe);
  if (recipe.success && (recipe.data.materials.length || (recipe.data.shape === "specification" && Number(recipe.data.supply_allowance) > 0))) return false;
  const approval = materialAllowanceApprovalSchema.safeParse(line.materialAllowanceApproval);
  if (approval.success && (approval.data.materials?.length || approval.data.builderWithheld || approval.data.source === "builder")) return false;
  const context = `${line.name} ${line.description ?? ""}`
    .replace(/\b(?:no|without|excluding)\s+(?:new\s+)?(?:suppl(?:y|ied)|materials?|replacement|reinstatement|making\s+good)\b/gi, "");
  return !/\b(?:supply|supplied|provide|install|installation|replace|replacement|rebuild|reinstate|reinstatement|infill|patch|repair|repairs|make\s+good|making\s+good)\b|\b(?:and|including|then|plus)\s+(?:plaster|paint|redecorate)\b/i.test(context);
}

type Line = {
  name: string; description?: string | null; category: string;
  quantity: number | string; unit: string; unitCost: number | string;
  materialCost?: number | string | null; isOptional?: boolean | null;
  quantityDerivation?: unknown; quoteCostRecipe?: unknown;
  materialAllowanceApproval?: unknown;
  costLibraryTaskId?: string | null;
  sourceVariant?: unknown;
};

/** Only the quote-local values whose change makes an approval stale. */
export function materialAllowanceBasis(line: Line): string {
  const data = line.quantityDerivation as { dimensions?: { id: string; label: string; value: number; unit: string; source: string }[] } | null;
  return JSON.stringify([
    line.name, line.description ?? "", line.category,
    Number(line.quantity), line.unit, Number(line.unitCost), Number(line.materialCost ?? 0),
    !!line.isOptional,
    (data?.dimensions ?? []).map(d => [d.id, d.label, d.value, d.unit, d.source]).sort((a, b) => String(a[0]).localeCompare(String(b[0]))),
  ]);
}

/** Purchasing identity excludes selling-price allocations and optional placement. */
export function materialProposalBasis(line: Line): string {
  return stableJson([materialAllowanceBasis({ ...line, unitCost: 0, materialCost: 0, isOptional: false }),
    line.quoteCostRecipe ?? null]);
}

function stableJson(value: unknown): string {
  return JSON.stringify(value, (_key, child) => child && typeof child === "object" && !Array.isArray(child)
    ? Object.fromEntries(Object.keys(child).sort().map(key => [key, child[key]])) : child);
}
function purchaseBasisMatches(basis: string, line: Line): boolean {
  // JSONB may return recipe keys in a different order; older identities remain compatible.
  try { return stableJson(JSON.parse(basis)) === materialProposalBasis(line); } catch { return false; }
}

/** A generic recipe is not a purchasing specification if it contradicts stated product dimensions. */
export function recipeProcurementSafe(line: Pick<Line, "name" | "description" | "quoteCostRecipe">): boolean {
  if (!line.quoteCostRecipe) return false;
  const parsed = costRecipeSchema.safeParse(line.quoteCostRecipe);
  if (!parsed.success) return false;
  const scopeMaterials = [...parsed.data.materials.map(m => ({ name: m.name, specification: m.spec })),
    ...(parsed.data.shape === "specification"
      ? [{ name: parsed.data.supply_name ?? "", specification: parsed.data.specification }] : [])];
  if (purchaseMaterialScopeConflict(line, scopeMaterials)) return false;
  const stated = materialSizes(`${line.name} ${line.description ?? ""}`);
  if (stated.length < 2) return true;
  const { recipe } = deriveCostRecipe(parsed.data);
  const specs = recipe.materials.map(material => `${material.spec ?? ""} ${material.dimension_pricing?.resolved_spec ?? ""}`);
  if (recipe.shape === "specification") specs.push(`${recipe.specification ?? ""} ${recipe.supply_pricing?.resolved_spec ?? ""}`);
  return specs.every(spec => {
    const candidate = materialSizes(spec);
    return candidate.length < 2 || stated.every(size => candidate.includes(size));
  });
}

export function materialSizes(text: string): number[] {
  const triple = text.match(/\b(\d{2,4})\s*[x×]\s*(\d{2,4})\s*[x×]\s*(\d{2,4})\b/i);
  if (triple) return triple.slice(1).map(Number);
  const pair = text.match(/\b(\d{2,4})\s*[x×]\s*(\d{2,4})\s*(?:mm\b|$)/i);
  if (pair) return pair.slice(1).map(Number);
  return [...text.matchAll(/\b(\d{2,4})\s*mm\b/gi)].map(match => Number(match[1]));
}

export function validMaterialAllowance(line: Line): MaterialAllowanceApproval | null {
  const parsed = materialAllowanceApprovalSchema.safeParse(line.materialAllowanceApproval);
  if (!parsed.success) return null;
  const approval = parsed.data;
  if (approval.recipeApproval) return null;
  const automatic = approval.mode === "automatic";
  if (line.quoteCostRecipe && !(automatic && (approval.requiresApproval || approval.source === "builder" || !recipeProcurementSafe(line)))) return null;
  if (!automatic && approval.approved === false) return null;
  if (automatic ? !purchaseBasisMatches(approval.basis, line) : approval.basis !== materialAllowanceBasis(line)) return null;
  // Legacy budget-only approvals must be reviewed again, never become shopping rows.
  if (!approval.materials || !materialProposalSchema.safeParse(approval).success) return null;
  if (Math.abs(proposedMaterialsTotal(approval.materials) - approval.amount) > .005) return null;
  return approval;
}

/** Approval of a recipe binds its actual quantities/specification, without copying or repricing it. */
export function validMaterialReview(line: Line): MaterialAllowanceApproval | null {
  const parsed = materialAllowanceApprovalSchema.safeParse(line.materialAllowanceApproval);
  if (!parsed.success) return null;
  if (parsed.data.recipeApproval) {
    return parsed.data.mode === "automatic" && !!line.quoteCostRecipe && recipeProcurementSafe(line)
      && purchaseBasisMatches(parsed.data.basis, line) ? parsed.data : null;
  }
  return validMaterialAllowance(line);
}

/** Never turn an invalidated explicit approval back into an implicitly approved legacy recipe on save. */
export function materialReviewForSave(line: Line): MaterialAllowanceApproval | null {
  const valid = validMaterialReview(line);
  if (valid) return valid;
  const parsed = materialAllowanceApprovalSchema.safeParse(line.materialAllowanceApproval);
  return parsed.success && parsed.data.mode === "automatic" ? { ...parsed.data, approved: false } : null;
}

/** High-confidence wrong-work breakdowns, not a product-name deduplication rule.
 * Shared by proposal validation and review of older automatic library matches.
 * Empty/labour-only lists and legitimate shared consumables are not duplicates.
 */
export function purchaseMaterialScopeConflict(
  line: { name: string; description?: string | null },
  materials: { name: string; specification?: string; spec?: string }[],
): boolean {
  if (!materials.length) return false;
  const name = line.name.toLowerCase();
  const supplied = materials.map(m => `${m.name} ${m.specification ?? m.spec ?? ""}`.toLowerCase());
  const has = (pattern: RegExp) => supplied.some(text => pattern.test(text));
  if (/\bbacker[\s-]?boards?\b/.test(name) &&
      !has(/\b(?:backer[\s-]?boards?|cement(?:itious)?[\s-]+boards?|tile[\s-]+boards?)\b/)) return true;
  if (/\bshaver\b/.test(name) && !has(/\bshaver\b/)) return true;
  if (/\b(?:tanking|waterproofing)\b/.test(name) &&
      has(/\b(?:ceramic|porcelain|floor|wall)[\s-]+tiles?\b/) &&
      !has(/\b(?:tanking|waterproof|membrane)\b/)) return true;
  if (/\b(?:electrical|electrics|wiring)\b/.test(name) &&
      has(/\b(?:timber|joist|framing nails)\b/) &&
      !has(/\b(?:cables?|wir(?:ing|e)|electrical|sockets?|switch(?:es)?|conduit|consumer unit|lighting)\b/)) return true;
  if (/\bskim\b/.test(name) && !/\b(?:plasterboard|boarding|boards?)\b/.test(name) &&
      !/\b(?:supply|install|fix|fit)\b.{0,40}\b(?:plasterboard|boarding|boards?)\b/i.test(line.description ?? "") &&
      has(/\b(?:plasterboard|wallboard)\b/)) return true;
  return false;
}

/** Flag a supplied specification which does not cover the stated material sizes. */
export function materialSpecificationNeedsReview(line: Line): boolean {
  const proposal = validMaterialAllowance(line);
  if (proposal?.source === "library" && purchaseMaterialScopeConflict(line, proposal.materials ?? [])) return true;
  if (!proposal && !recipeProcurementSafe(line)) return !!line.quoteCostRecipe;
  const stated = materialSizes(`${line.name} ${line.description ?? ""}`);
  const parsed = costRecipeSchema.safeParse(line.quoteCostRecipe);
  const recipe = parsed.success ? deriveCostRecipe(parsed.data).recipe : null;
  const supplied = proposal
    ? proposal.materials!.flatMap(m => materialSizes(`${m.name} ${m.specification}`))
    : [...(recipe?.materials ?? []).flatMap(m => materialSizes(`${m.name} ${m.spec ?? ""} ${m.dimension_pricing?.resolved_spec ?? ""}`)),
      ...(recipe?.shape === "specification"
        ? materialSizes(`${recipe.specification} ${recipe.supply_pricing?.resolved_spec ?? ""}`) : [])];
  return stated.length >= 2 && supplied.length > 0 && !stated.every(size => supplied.includes(size));
}

/** Only explicitly accepted suggestions get an undo control, not automatic
 * library rows or recipe markers created by adjusting an order quantity. */
export function manuallyApprovedMaterials(line: Line): boolean {
  const review = validMaterialReview(line);
  return review?.approved === true && (
    review.manualApproved === true ||
    review.source === "estimated" ||
    (review.source === "builder" &&
      (!review.recipeApproval || materialSpecificationNeedsReview(line)))
  );
}

/** Complete attached breakdowns are included automatically; explicit builder
 * drafts, stale identities and incomplete/conflicting suggestions still need confirmation. */
export function materialsApproved(line: Line, requireApproval = false): boolean {
  // Ignore obsolete recipe-approval markers from the former all-items gate.
  // A real quote-local replacement must still be approved independently.
  const stored = materialAllowanceApprovalSchema.safeParse(line.materialAllowanceApproval);
  // An explicit undo survives later quantity/spec edits until the builder confirms again.
  if (stored.success && stored.data.builderWithheld) return false;
  // A user-requested lookup is a suggestion, including library matches and
  // positive-price estimates. Its provenance must never bypass approval.
  if (stored.success && stored.data.requiresApproval && stored.data.approved !== true) return false;
  const replacement = stored.success && stored.data.mode === "automatic" && !stored.data.recipeApproval;
  const review = validMaterialReview(line);
  if (materialSpecificationNeedsReview(line)) return review?.approved === true && review.source !== "library";
  if (recipeProcurementSafe(line) && !replacement) return true;
  if (review) {
    if (review.approved === true) return true;
    // An explicit no-materials breakdown is not a purchase awaiting approval.
    if (review.materials?.length === 0 && review.noMaterialsReason?.trim()) return true;
    if (materialSpecificationNeedsReview(line)) return false;
    if (review.source === "estimated" && review.materials?.length &&
      review.materials.every(material => material.unitPrice > 0)) return true;
    return review.source === "library" || (review.approved ?? (review.mode !== "automatic" || !requireApproval));
  }
  if (requireApproval) return false;
  if (line.materialAllowanceApproval == null) return true;
  const legacy = materialAllowanceApprovalSchema.safeParse(line.materialAllowanceApproval);
  return legacy.success && legacy.data.mode !== "automatic" && recipeProcurementSafe(line);
}
